import * as React from 'react';
import { useMemo } from 'react';
import { css } from '@emotion/core';
import Card from '../../../layout/card/Card';
import CardHeader from '../../../layout/card/CardHeader';
import { faExclamationTriangle, faEnvelope, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import BulletedMsg from '../../../layout/BulletedMsg';
import { COLORS } from '../../../globalStyle/colors';
import EmailDevPlanForm from './EmailDevPlanForm';
import { useAppState } from '../../../core/redux/hooks';
import { isNilOrEmpty } from '../../../language/module';
import ClamShell from '../../../layout/ClamShell';
import { Rater } from '../../survey/raters/models';


const contentSideMargin = css`
  margin-left: 14px;
  margin-right: 14px;
`;

const styles = {
  root:          css`
    padding: 0;
    overflow: hidden;
  `,
  header:        css`
    padding-top: 14px;
    margin-left: 14px;
    margin-right: 14px;
    margin-bottom: 28px;
  `,
  emailRequired: {
    icon: css`
      font-size: 24px;
    `,
    txt:  [
      contentSideMargin,
      css`
        color: ${COLORS.GREEN};
        margin-bottom: 18px;
      `,
    ],
  },
  emailSent:     {
    icon: css`
      font-size: 24px;
      color: ${COLORS.BLUE};
    `,
    txt:  [
      contentSideMargin,
      css`
        color: ${COLORS.DARK_GREY};
        margin-bottom: 18px;
      `,
    ],
  },
  emailForm:     [
    contentSideMargin,
    css`
      margin-bottom: 14px;
    `,
  ],
};


type Props = {
  participantId: number;
  projectId: number;
  emailingRequired: boolean;
  raters: Rater[];

  onClickSubmitBtn: (email: string, comment?: string) => void;
  submitBtnMsg: string;

  collaborationEnabled: boolean;

  className?: string;
};


/**
 * EmailDevPlanCard: a component that renders a form and header in a card for sending dev plans to people by email.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const EmailDevPlanCard: React.FC<Props> = props => {
  const title = useMsgText(props.collaborationEnabled ? Str.SharePlan : Str.EmailPlan);

  const lastEmailed = useAppState(state => state.devPlan?.lastEmailRecipient);

  const requiredLevelId = useAppState(state => state.projectSummary?.developmentPlanEmailLevelId);
  const levelName       = useAppState(state => state.projectSummary?.devPlanEmailLevelName ?? '');
  const raters          = useMemo(
    () => props.raters.filter(rater => rater.levelId === requiredLevelId && !isNilOrEmpty(rater.email)),
    [requiredLevelId, props],
  );

  const showSentMsg = useMemo(
    () => !isNilOrEmpty(lastEmailed),
    [lastEmailed],
  );

  const sentMsg = useMsgText(
    props.collaborationEnabled ? Str.InviteSent : Str.PlanSentTo,
    '',
    { 'email': lastEmailed },
  );

  const reqMsg = useMsgText(
    props.collaborationEnabled ? Str.DevPlanShareMsg : Str.DevPlanEmailMsg,
    '',
    { 'levelName': levelName },
  );

  return (
    <Card css={styles.root} className={props.className}>
      <CardHeader title={title} icon={faEnvelope} css={styles.header} />

      {/* Email Required */}
      <ClamShell show={props.emailingRequired && !showSentMsg}>
        <BulletedMsg
          icon={faExclamationTriangle}
          msg={reqMsg}
          iconStyle={styles.emailRequired.icon}
          css={styles.emailRequired.txt}
        />
      </ClamShell>

      { /* Email Sent */}
      <ClamShell show={showSentMsg}>
        <BulletedMsg
          icon={faCheck}
          msg={sentMsg}
          iconStyle={styles.emailSent.icon}
          css={styles.emailSent.txt}
        />
      </ClamShell>

      <EmailDevPlanForm
        key={lastEmailed}
        raters={raters}
        participantId={props.participantId}
        projectId={props.projectId}
        emailingRequired={props.emailingRequired}
        onClickSubmitBtn={props.onClickSubmitBtn}
        submitBtnMsg={props.submitBtnMsg}
        css={styles.emailForm}
      />
    </Card>
  );
};

export default EmailDevPlanCard;
